import { Backdrop, Box, Fade, Modal } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";

import isCleaner from "../../HOC/IsCleaner";
import CustomerBioShort from "../../components/CustomerBioShort/CustomerBioShort";
import Loader from "../../components/Loader/Loader";
import ModalPhoneVerification from "../../components/ModalPhoneVerification/ModalPhoneVerification";
import ModalReVerification from "../../components/ModalReVerification/ModalReVerification";
import Pagination from "../../components/Pagination/Pagination";
import SearchBar from "../../components/SearchBar/SearchBar";
import noResultsFilters from '../../constants/images/no-results-filter.svg';
import { verificationService } from "../../services/verification.service";
import { cleaningRequestsActions } from "../../store/slices/cleaningRequestsSlice";
import EmptyState from "../EmptyState/EmptyState";
import styles from "./AllCleaningRequests.module.scss";

const AllCleaningRequests = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [needsReverification, setNeedsReverification] = useState(false);
    const [reverificationReason, setReverificationReason] = useState('');
    const [reverification, setReverification] = useState(null);
    const [needsPhoneVerification, setNeedsPhoneVerification] = useState(false);

    const dispatch = useDispatch();
    const {allCleaningRequests, status, error} = useSelector(state => state.cleaningRequests);
    const {
        min_price,
        max_price,
        ordering,
        trigger,
        searchValue,
        package_names
    } = useSelector(state => state.filtersForCleaner);


    useEffect(() => {
        if (error) {
            setCurrentPage(1);
            // getFilteredActiveCleaningRequests();
            dispatch(cleaningRequestsActions.clearError());
        }
    }, [error]);

    // This page is cleaner-only (wrapped in the isCleaner HOC). On mount, check
    // whether the cleaner's documents were rejected and a new verification
    // attempt is required; if so, prompt them to re-submit. needs_reverification
    // is true only when the backend actually wants new documents, so we don't
    // prompt for approved / pending-with-docs states.
    useEffect(() => {
        let isMounted = true;

        const checkReverification = async () => {
            try {
                const { data } = await verificationService.getStatus();

                if (isMounted && data?.needs_reverification) {
                    setNeedsReverification(true);
                    setReverificationReason(data?.verification?.reason || '');
                    setReverification(data?.verification || null);
                }
                // Phone OTP is only prompted once the backend requires it
                // (REQUIRE_PHONE_FOR_BID) and the cleaner hasn't verified yet.
                if (isMounted && data?.phone_required && !data?.is_phone_verified) {
                    setNeedsPhoneVerification(true);
                }
            } catch (err) {
                console.error(err);
            }
        };

        checkReverification();

        return () => {
            isMounted = false;
        };
    }, []);

    const getFilteredActiveCleaningRequests = async () => {
        try {
            if (package_names) {
                const params = {
                    status: "CREATED",
                    page: currentPage,
                    min_price,
                    max_price,
                    ordering,
                    search: searchValue,
                    package_names
                };
                await dispatch(cleaningRequestsActions.getFilteredActiveCleaningRequests({params}));
            } else {
                const params = {
                    status: "CREATED",
                    page: currentPage,
                    min_price,
                    max_price,
                    ordering,
                    search: searchValue,
                };
                await dispatch(cleaningRequestsActions.getFilteredActiveCleaningRequests({params}));
            }
        } catch (error) {
            setCurrentPage(1);
            if (currentPage === 1) {
                getFilteredActiveCleaningRequests();
            }
            console.error(error);
        }
    }

    useEffect(() => {
        // const getAllActiveCleaningRequests = async () => {
        //     try {
        //         await dispatch(cleaningRequestsActions.getActiveCleaningRequests({page: currentPage}));
        //     } catch (error) {
        //         console.error(error);
        //     }
        // }


        // if (!ordering && (searchValue || package_names || max_price || min_price)) {
        getFilteredActiveCleaningRequests();
        // } else {
        //     getAllActiveCleaningRequests();
        // }
    }, [currentPage, ordering, trigger, package_names]);

    if (status === 'loading') {
        return <Loader/>;
    }

    function filterByDate(date) {
        return allCleaningRequests.filter((cleaningReq) => {
            const reqDate = new Date(cleaningReq?.request_datetime);
            return reqDate > date;
        });
    }

    const currentDate = new Date(); 
    const filteredRequests = filterByDate(currentDate);

    return (
        <div className={styles.allCleaningRequests_wrapper}>
            <div className={styles.allCleaningRequests}>
                <h1 className={styles.allCleaningRequests_title}>Замовлення</h1>

                <div className={styles.allCleaningRequests_pagination}>
                    <div className={styles.allCleaningRequests_pagination_content}>
                        <SearchBar type={'cleaner'} page={currentPage} setPage={setCurrentPage}/>

                        {filteredRequests?.map((cleaningRequest, index) =>
                            <CustomerBioShort
                                key={index}
                                order={cleaningRequest}
                                type={'apply'}
                                offerId={null}
                                application={null}
                            />)}
                        {filteredRequests?.length === 0 &&
                            <EmptyState
                                description={'Ми не змогли знайти результатів за вашими критеріями. Спробуйте змінити ваш запит.'}
                                title={'За вашим запитом результатів не знайдено'}
                                icon={noResultsFilters}
                            />}
                    </div>

                    {!(filteredRequests?.length === 0) &&
                        <Pagination
                            type={'cleaning-request'}
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                        />}
                </div>
            </div>

            <Modal
                aria-labelledby="reverification-modal-title"
                aria-describedby="reverification-modal-description"
                open={needsReverification}
                onClose={() => setNeedsReverification(false)}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{ backdrop: { timeout: 500 } }}
            >
                <Fade in={needsReverification}>
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        outline: 'none',
                    }}>
                        <ModalReVerification reason={reverificationReason} verification={reverification} />
                    </Box>
                </Fade>
            </Modal>

            <Modal
                aria-labelledby="phone-verification-modal-title"
                aria-describedby="phone-verification-modal-description"
                open={!needsReverification && needsPhoneVerification}
                onClose={() => setNeedsPhoneVerification(false)}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{ backdrop: { timeout: 500 } }}
            >
                <Fade in={!needsReverification && needsPhoneVerification}>
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        outline: 'none',
                    }}>
                        <ModalPhoneVerification
                            onVerified={() => setNeedsPhoneVerification(false)}
                        />
                    </Box>
                </Fade>
            </Modal>
        </div>
    );
};

export default isCleaner(AllCleaningRequests);
